import { Stack, Text } from '@chakra-ui/react'
import { useCallback, useMemo, useState } from 'react'

import { useUser } from '@/client/context/user'
import { RPCRefresh } from '@/client/rpc'

import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'

export default function useCohostRemoveSelfModal(): [
  ({
    hostId,
    mainHostId,
    isSession,
  }: {
    hostId: number | null
    mainHostId: number | null
    isSession: boolean
  }) => JSX.Element | null,
  () => void,
] {
  const [showModal, setShowModal] = useState(false)

  const CohostRemoveSelfModal = ({
    hostId,
    mainHostId,
    isSession,
  }: {
    hostId: number | null
    mainHostId: number | null
    isSession: boolean
  }) => {
    const toast = useToaster()
    const user = useUser()

    const removeSelfCohostInputs: FormInputType[] = useMemo(
      () => [
        {
          label: 'Write a message (optional)',
          type: 'textarea',
          name: 'reason',
          placeholder: 'Let them know why you can no longer co-host.',
          required: false,
          size: 6,
        },
      ],
      []
    )

    const handleSubmit = useCallback(
      async (values: FormValues) => {
        if (isSession) {
          await RPCRefresh.removeSessionHost({
            sessionHostId: hostId ?? 0,
            deletedMessage: `${values.reason}`,
          })
        } else {
          await RPCRefresh.removeSeriesHost({
            seriesHostId: hostId ?? 0,
            deletedMessage: `${values.reason}`,
          })
        }
        toast({
          title: 'You removed yourself as a co-host.',
          status: 'success',
        })
        setShowModal(false)
      },
      [toast, hostId, isSession]
    )

    if (user.id === mainHostId) {
      return null
    }
    return (
      <ModalV3
        size="2xl"
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Remove Self as Co-host"
        body={() => (
          <Stack spacing="18px">
            <Text>
              No longer interested in Co-hosting? We&apos;ll let the primary host now. If you change
              your mind, you will need to request to co-host again.
            </Text>
            <Form name="CohostSelfRemove" blocks={removeSelfCohostInputs} onSubmit={handleSubmit} />
          </Stack>
        )}
      />
    )
  }
  return [CohostRemoveSelfModal, () => setShowModal(true)]
}
